/**
 * Notifications Page - Type-Safe Version
 * Lists user notifications and supports mark-as-read actions
 */
import { buildLoginRedirect, getCurrentAuthContext, getCurrentPath, isAuthErrorMessage, protectedPage } from '../lib/page-runtime';

function initNotificationsPage() {
  'use strict';

  const maybeNotificationsListEl = document.getElementById('notifications-list');
  if (!maybeNotificationsListEl) return;
  const notificationsListEl: HTMLElement = maybeNotificationsListEl;
  let currentFilter = window.NotificationsPageData.notificationFilterFromSearch(window.location.search);
  let markingAllRead = false;
  const maxPrefetchedIssueNotifications = 6;

  function updateUrlForFilter(): void {
    const search = window.NotificationsPageData.buildNotificationFilterSearch(currentFilter);
    const nextUrl = window.location.pathname + search;
    if (window.location.pathname + window.location.search !== nextUrl) {
      window.history.replaceState(null, '', nextUrl);
    }
  }

  function syncFilterControls(unreadCount: number): void {
    const filterButtons = document.querySelectorAll<HTMLButtonElement>('[data-notification-filter]');
    filterButtons.forEach(function(btn) {
      const filter = btn.dataset.notificationFilter === 'unread' ? 'unread' : 'all';
      const active = filter === currentFilter;
      btn.className = active
        ? 'inline-flex h-8 items-center rounded border border-brand-700 bg-brand-950/30 px-3 text-xs font-medium text-brand-200'
        : 'inline-flex h-8 items-center rounded border border-zinc-800 bg-zinc-950/40 px-3 text-xs font-medium text-zinc-400 hover:border-zinc-700 hover:text-zinc-200';
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (filter === 'unread') {
        btn.textContent = unreadCount > 0 ? `Unread (${unreadCount})` : 'Unread';
      }
    });
  }

  function prefetchVisibleIssueNotifications(items: readonly { link?: string | null }[]): void {
    let prefetched = 0;
    for (const item of items) {
      if (prefetched >= maxPrefetchedIssueNotifications) return;
      if (window.NotificationsPageData.prefetchIssuePageDataFromNotificationLink(item.link)) {
        prefetched += 1;
      }
    }
  }

  function scheduleIssueNotificationPrefetch(items: readonly { link?: string | null }[]): void {
    const snapshot = [...items];
    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number;
    };
    if (typeof idleWindow.requestIdleCallback === 'function') {
      idleWindow.requestIdleCallback(() => prefetchVisibleIssueNotifications(snapshot), { timeout: 1500 });
      return;
    }
    queueMicrotask(() => prefetchVisibleIssueNotifications(snapshot));
  }

  async function loadNotifications() {
    // Keep this guard for stale-token races between /api/auth/me and /api/notifications.
    if (!getCurrentAuthContext().loggedIn) {
      window.location.href = buildLoginRedirect(getCurrentPath());
      return;
    }

    try {
      const result = await window.NotificationsPageData.loadNotifications(currentFilter);

      if (!result.ok) {
        if (isAuthErrorMessage(result.error)) {
          window.location.href = buildLoginRedirect(getCurrentPath());
          return;
        }
        notificationsListEl.innerHTML =
          '<p class="text-center text-zinc-500 py-8">Failed to load notifications.</p>';
        return;
      }

      const data = result.data;
      const items = data.items;
      notificationsListEl.innerHTML = window.NotificationsPageRenderers.renderNotifications(items, { filter: currentFilter });
      scheduleIssueNotificationPrefetch(items);
      const summaryEl = document.getElementById('notifications-summary');
      if (summaryEl) {
        summaryEl.textContent = window.NotificationsPageRenderers.renderNotificationSummary(data);
      }
      syncFilterControls(data.unreadCount);
      updateUrlForFilter();

      const hasUnread = items.some(function(n) { return !n.is_read; });
      const markAllBtn = document.getElementById('mark-all-read-btn');
      if (markAllBtn) {
        markAllBtn.innerHTML = hasUnread ? '<i class="fas fa-check-double mr-1"></i>Mark all read' : '';
        markAllBtn.style.display = hasUnread ? '' : 'none';
        markAllBtn.setAttribute('aria-hidden', hasUnread ? 'false' : 'true');
      }
    } catch (e) {
      const el = document.getElementById('notifications-list');
      if (el) {
        el.innerHTML = '<p class="text-center text-red-400 py-8">Failed to load notifications. Please try refreshing.</p>';
      }
    }
  }

  function updateMarkAllVisibility() {
    const markAllBtn = document.getElementById('mark-all-read-btn');
    if (!markAllBtn) return;
    const hasUnread = Boolean(document.querySelector('[id^="notif-"].border-brand-500'));
    markAllBtn.innerHTML = hasUnread ? '<i class="fas fa-check-double mr-1"></i>Mark all read' : '';
    markAllBtn.style.display = hasUnread ? '' : 'none';
    markAllBtn.setAttribute('aria-hidden', hasUnread ? 'false' : 'true');
  }

  function markAllReadToast(count: number): string {
    if (count <= 0) return 'No unread notifications to mark';
    return count === 1 ? '1 notification marked as read' : `${count} notifications marked as read`;
  }

  function setMarkAllButtonBusy(button: HTMLButtonElement | null): () => void {
    if (!button) return () => {};
    const originalHtml = button.innerHTML;
    const originalDisabled = button.disabled;
    const originalAriaBusy = button.getAttribute('aria-busy');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.innerHTML = '<i class="fas fa-spinner fa-spin mr-1"></i>Marking...';
    return function restoreMarkAllButton() {
      if (!button.isConnected) return;
      button.disabled = originalDisabled;
      button.innerHTML = originalHtml;
      if (originalAriaBusy === null) {
        button.removeAttribute('aria-busy');
      } else {
        button.setAttribute('aria-busy', originalAriaBusy);
      }
    };
  }

  async function markNotificationReadInUi(notifId: number, options?: { showError?: boolean }): Promise<void> {
    try {
      const result = await window.NotificationsPageData.markNotificationRead(notifId);
      if (!result.ok) throw new Error(result.error);
      if (currentFilter === 'unread') {
        await loadNotifications();
        window.refreshNotificationBadge && await window.refreshNotificationBadge();
        return;
      }
      const el = document.getElementById('notif-' + notifId);
      if (el) {
        el.classList.remove('border-l-2', 'border-brand-500', 'bg-zinc-800/50');
        el.classList.add('bg-zinc-900/30');
        const btn = el.querySelector('button[onclick^="markOneRead"]');
        if (btn) btn.remove();
        const viewLink = el.querySelector('a[onclick^="return markNotificationLinkRead"]');
        if (viewLink) viewLink.removeAttribute('onclick');
      }
      updateMarkAllVisibility();
      window.refreshNotificationBadge && await window.refreshNotificationBadge();
    } catch (e) {
      if (options?.showError !== false) showToast('Failed to mark as read', 'error');
      throw e;
    }
  }

  window.markOneRead = async function(notifId: number) {
    try {
      await markNotificationReadInUi(notifId, { showError: true });
    } catch (_e) {
      // Error toast is handled by markNotificationReadInUi.
    }
  };

  window.setNotificationFilter = async function(filter: 'all' | 'unread') {
    currentFilter = filter;
    await loadNotifications();
  };

  window.markNotificationLinkRead = function(event: Event, notifId: number, href: string): boolean {
    event.preventDefault();
    const actionStatus = document.getElementById('notif-' + notifId + '-action-status');
    if (actionStatus) {
      const action = window.NotificationsPageRenderers.notificationDestinationAction(href);
      actionStatus.textContent = action.openingStatusLabel;
      actionStatus.classList.remove('text-zinc-600');
      actionStatus.classList.add('text-brand-300');
    }
    window.NotificationsPageData.markNotificationReadInBackground(notifId);
    window.location.href = href;
    return false;
  };

  window.markAllNotificationsRead = async function() {
    if (markingAllRead) return;
    markingAllRead = true;
    const markAllBtn = document.getElementById('mark-all-read-btn') as HTMLButtonElement | null;
    const restoreMarkAllButton = setMarkAllButtonBusy(markAllBtn);
    try {
      const result = await window.NotificationsPageData.markAllNotificationsRead();
      if (!result.ok) throw new Error(result.error);
      showToast(markAllReadToast(result.data.count));
      restoreMarkAllButton();
      await loadNotifications();
      window.refreshNotificationBadge && window.refreshNotificationBadge();
    } catch (e) {
      restoreMarkAllButton();
      showToast('Failed to mark all as read', 'error');
    } finally {
      markingAllRead = false;
    }
  };

  protectedPage({
    redirectTo: 'current',
    init: loadNotifications,
  });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  initNotificationsPage();
}
